import { Component } from 'react'
import { toast } from 'react-toastify'
import { ClientModel, getClientById, IClientUID } from '../../../api/clients'
import { getString } from '../../../locale'
import Loader from '../../../components/loader'
import {
    cancelClientC6HttpOta,
    cancelClientHttpOta,
    getClientC6HttpOtaStatus,
    getClientC6RawOtaStatus,
    getClientHttpOtaStatus,
    HttpOtaStatus,
    startClientC6FirmwarePatch,
    startClientC6HttpFirmwarePatchFromBin,
    startClientFirmwarePatch,
    startClientHttpFirmwarePatchFromBin,
    stopClientC6FirmwarePatch,
    stopClientFirmwarePatch,
    isPatchingClient,
    StartErrorCode,
    isRestartingClient,
} from '../../../api/0x0001/ota'
import { getAllSoftwareVersions, getSoftwareVersion } from '../../../api/0x0001/software-versions'
import { MyFile } from '../../../components/files'
import { ProgressBar } from '../../../components/progressbar'
import classNames from 'classnames'
import { clearOtaProgress, onOtaProgress } from '../../../wsclient/handlers/machine-ota-progress.handler'
import { clearOtaWriteError, onOtaWriteError, OtaWriteErrorCode } from '../../../wsclient/handlers/machine-ota-write-error.handler'
import { EspBinInput } from '../../../components/esp-bin-input'
import { ChipId, IEspApp } from '../../../components/esp-bin-input/esp-bin-parser'
import { getClientStaticTraits, IStaticTraits } from '../../../api/0x0001/statictraits'
import { ISoftwareVersion, isSoftwareOutdated } from '../../../api/0x0002/software-versions'
import { askSoftwareVersion } from '../../../window/ask-software-version'
import { ChipModel } from '../../../constants/chip-model.enum'

type Props = {
    clientId: IClientUID
}

type State = {
    patching?: boolean
    restarting?: boolean
    binFile?: MyFile
    base64Bin?: string
    progress?: number
    outdated: boolean
    latestSoftwareVersion?: ISoftwareVersion
    staticTraits?: IStaticTraits
    clientModel?: ClientModel
    otaTransport?: 'legacy' | 'http'
    httpOtaState?: number
    httpOtaMessage?: string
    // ESP32-C6 (network_adapter coprocessor) — LTSP101/LTSP70 machines only.
    c6Supported?: boolean
    c6Versions: ISoftwareVersion[]
    c6SelectedId?: number
    c6Updating?: boolean
    c6Transport?: 'http' | 'push'
    c6Progress?: number
    c6Message?: string
}

class OtaTab extends Component<Props, State> {
    state: State = this.getInitialState()
    otaProgressListenerId?: number
    otaWriteErrorListenerId?: number
    httpOtaPollRef?: ReturnType<typeof setInterval>
    c6PollRef?: ReturnType<typeof setInterval>
    c6RawPollRef?: ReturnType<typeof setInterval>

    espBinRef: EspBinInput | null = null

    private getInitialState(): State {
        return {
            patching: undefined,
            binFile: undefined,
            base64Bin: undefined,
            progress: undefined,
            outdated: false,
            latestSoftwareVersion: undefined,
            otaTransport: undefined,
            httpOtaState: undefined,
            httpOtaMessage: undefined,
            c6Supported: false,
            c6Versions: [],
            c6Transport: undefined,
        }
    }

    componentDidMount = async () => {
        try {
            const clientResult = (await getClientById(this.props.clientId)).data
            const patchingResult = (await isPatchingClient(this.props.clientId)).data
            const restartingResult = (await isRestartingClient(this.props.clientId)).data
            const staticTraitsResult = (await getClientStaticTraits(this.props.clientId)).data
            const c6Supported = clientResult.model === ClientModel.LTSP101 || clientResult.model === ClientModel.LTSP70
            this.setState({
                clientModel: clientResult.model,
                patching: patchingResult,
                restarting: restartingResult,
                staticTraits: staticTraitsResult,
                c6Supported,
            })
            if (patchingResult) {
                await this.tryAttachToActiveHttpOta()
            }
            await this.checkVersion()
            if (c6Supported) {
                await this.loadC6Versions()
            }
            if (restartingResult) {
                toast.success(getString('ota.success_message'))
            }
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
        this.otaProgressListenerId = onOtaProgress(this.handleOtaProgress)
        this.otaWriteErrorListenerId = onOtaWriteError(this.handleOtaWriteError)
    }

    componentWillUnmount = () => {
        if (this.otaProgressListenerId !== undefined) {
            clearOtaProgress(this.otaProgressListenerId)
        }
        if (this.otaWriteErrorListenerId !== undefined) {
            clearOtaWriteError(this.otaWriteErrorListenerId)
        }
        this.stopHttpOtaStatusPolling()
        this.stopC6Polling()
        this.stopC6RawPolling()
    }

    private checkVersion = async () => {
        try {
            if (!this.state.clientModel) return
            const staticTraits = (await getClientStaticTraits(this.props.clientId)).data
            const versionCheck = (await isSoftwareOutdated(staticTraits.version, this.state.clientModel)).data
            this.setState({
                outdated: versionCheck.outdated,
                latestSoftwareVersion: versionCheck.softwareVersion,
            })
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    private handleOtaProgress = (clientId: IClientUID, progress: number) => {
        if (clientId === this.props.clientId) {
            // C6 raw socket push (Flow B) — the server streams the image and
            // reports progress over the same ws events. On 100 the machine is
            // activating the C6 and will restart itself — do not touch the P4
            // patching/restarting state.
            if (this.state.c6Transport === 'push' && this.state.c6Updating === true) {
                if (progress === 100) {
                    this.stopC6RawPolling()
                    this.setState({ c6Updating: false, c6Transport: undefined, c6Progress: 100, c6Message: undefined })
                    toast.success(getString('ota.c6_completed'))
                } else {
                    this.setState({ c6Progress: progress })
                }
                return
            }
            if (this.state.otaTransport === 'http') return
            if (progress === 100) {
                this.setState({ progress: 0, patching: false, restarting: true, otaTransport: undefined })
                toast.success(getString('ota.success_message'))
                this.checkVersion()
            } else {
                this.setState({ progress, patching: true })
            }
        }
    }

    private handleOtaWriteError = (clientId: IClientUID, errorCode: OtaWriteErrorCode) => {
        if (clientId === this.props.clientId) {
            switch (errorCode) {
                case OtaWriteErrorCode.AlreadyActive:
                    toast.error(getString('ota.write_err_already_active'))
                    break
                case OtaWriteErrorCode.BeginFailed:
                    toast.error(getString('ota.write_err_begin_failed'))
                    break
                case OtaWriteErrorCode.CurrentRunningVersionSameAsNew:
                    toast.error(getString('ota.write_err_current_running_version_same_as_new'))
                    this.setState({ patching: false, progress: undefined, otaTransport: undefined })
                    break
                case OtaWriteErrorCode.NewVersionSameAsInvalidVersion:
                    toast.error(getString('ota.write_err_new_version_same_as_invalid_version'))
                    this.setState({ patching: false, progress: undefined, otaTransport: undefined })
                    break
                case OtaWriteErrorCode.ReceivedPackageNotFitLen:
                    toast.error(getString('ota.write_err_received_package_not_fit_len'))
                    break
                case OtaWriteErrorCode.WriteFailed:
                    toast.error(getString('ota.write_err_write_failed'))
                    break
                case OtaWriteErrorCode.Ok:
                    break
            }
        }
    }

    private handleSoftwareSelectButton = async () => {
        if (!this.state.clientModel) return
        const softwareVersion = await askSoftwareVersion(this.state.clientModel)
        if (softwareVersion === undefined) return

        console.log('softwareVersion', softwareVersion)

        const buffer = Buffer.from(softwareVersion.base64Bin, 'base64')
        const file = new Blob([buffer], { type: 'application/octet-binary' }) as MyFile
        file.id = softwareVersion.version
        this.setState({
            binFile: file,
            base64Bin: softwareVersion.base64Bin,
        })
    }

    private tryAttachToActiveHttpOta = async () => {
        try {
            const status = (await getClientHttpOtaStatus(this.props.clientId)).data
            if ([1, 2].includes(status.state)) {
                this.setState({
                    otaTransport: 'http',
                    progress: status.progressPercent,
                    httpOtaState: status.state,
                    httpOtaMessage: status.message,
                })
                this.startHttpOtaStatusPolling()
            }
        } catch {
            // Ignore: either legacy OTA or firmware does not support HTTP OTA status.
        }
    }

    private handleStartLegacyButtonClick = async () => {
        try {
            if (!this.state.staticTraits) return
            if (!this.espBinRef || !this.espBinRef.state.espApp) return
            if (ChipModel[this.state.staticTraits.model] !== ChipId[this.espBinRef.state.espApp.chipId]) {
                throw new Error(
                    getString('ota.bin_model_mismatch', ChipModel[this.state.staticTraits.model], ChipId[this.espBinRef.state.espApp.chipId]),
                )
            }

            const result = await startClientFirmwarePatch(this.props.clientId, this.state.base64Bin!)
            console.log('result', result)
            if (result.data === StartErrorCode.AlreadyPatching) {
                throw new Error(getString('ota.start_err_already_patching'))
            } else if (result.data === StartErrorCode.NotInSync) {
                throw new Error(getString('ota.start_err_not_in_sync'))
            }
            this.setState({ patching: true, progress: 0 })
            this.setState({ otaTransport: 'legacy' })
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    private handleStartHttpButtonClick = async () => {
        try {
            const base64Bin = this.state.base64Bin
            if (!base64Bin) throw new Error(getString('ota.select_software'))
            const result = await startClientHttpFirmwarePatchFromBin(this.props.clientId, base64Bin)
            if (result.data.success !== 1) {
                throw new Error(result.data.message || 'HTTP OTA start failed')
            }
            this.setState({
                patching: true,
                otaTransport: 'http',
                httpOtaState: undefined,
                httpOtaMessage: result.data.message,
                progress: 0,
            })
            this.startHttpOtaStatusPolling()
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    private handleStopButtonClick = async () => {
        try {
            if (this.state.otaTransport === 'http') {
                const result = await cancelClientHttpOta(this.props.clientId)
                console.log('result', result)
                this.stopHttpOtaStatusPolling()
                this.setState({ patching: false, progress: undefined, otaTransport: undefined })
            } else {
                const result = await stopClientFirmwarePatch(this.props.clientId)
                console.log('result', result)
                this.setState({ patching: false, progress: undefined, otaTransport: undefined })
            }
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    private applyHttpStatus(status: HttpOtaStatus) {
        this.setState({
            httpOtaState: status.state,
            httpOtaMessage: status.message,
            progress: status.progressPercent,
        })
        if (status.state === 3) {
            this.stopHttpOtaStatusPolling()
            this.setState({ patching: false, restarting: true, otaTransport: undefined, progress: 100 })
            toast.success(getString('ota.success_message'))
            this.checkVersion()
            return
        }
        if (status.state === 4) {
            this.stopHttpOtaStatusPolling()
            this.setState({ patching: false, otaTransport: undefined })
            throw new Error(status.message || 'HTTP OTA failed')
        }
        if (status.state === 5) {
            this.stopHttpOtaStatusPolling()
            this.setState({ patching: false, otaTransport: undefined })
            toast.info(status.message || 'HTTP OTA cancelled')
        }
    }

    private startHttpOtaStatusPolling() {
        this.stopHttpOtaStatusPolling()
        this.httpOtaPollRef = setInterval(async () => {
            try {
                const status = (await getClientHttpOtaStatus(this.props.clientId)).data
                this.applyHttpStatus(status)
            } catch (err) {
                console.error(err)
                this.stopHttpOtaStatusPolling()
                this.setState({ patching: false, otaTransport: undefined })
                toast.error((err as Error).message)
            }
        }, 1500)
    }

    private stopHttpOtaStatusPolling() {
        if (this.httpOtaPollRef !== undefined) {
            clearInterval(this.httpOtaPollRef)
            this.httpOtaPollRef = undefined
        }
    }

    // ----------------------------------------------------------------------
    // ESP32-C6 (network_adapter coprocessor) — URL-handoff OTA
    // The machine downloads the image itself; states mirror the P4 HTTP flow:
    // 0 idle, 1 downloading, 2 finalizing, 3 completed, 4 failed, 5 cancelled.
    // On state 3 the C6 reboots and ~1.5-3.5s later the whole machine restarts
    // to resync ESP-Hosted — the link drops, which is expected. Never wait for
    // (or cancel after) state 3.
    // ----------------------------------------------------------------------

    private loadC6Versions = async () => {
        if (!this.state.clientModel) return
        try {
            const result = await getAllSoftwareVersions(this.state.clientModel, 'c6')
            this.setState({
                c6Versions: result.data,
                c6SelectedId: result.data.length > 0 ? result.data[0].id : undefined,
            })
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    private handleC6StartButtonClick = async () => {
        try {
            const { c6SelectedId } = this.state
            if (c6SelectedId === undefined) throw new Error(getString('ota.select_software'))
            const full = (await getSoftwareVersion(c6SelectedId)).data
            const base64Bin = full.base64Bin
            if (!base64Bin) throw new Error(getString('ota.select_software'))
            const result = await startClientC6HttpFirmwarePatchFromBin(this.props.clientId, base64Bin)
            console.log('startClientC6HttpFirmwarePatchFromBin', result)
            if (result.data.success !== 1) {
                throw new Error(result.data.message || 'C6 update start failed')
            }
            this.setState({
                c6Updating: true,
                c6Transport: 'http',
                c6Progress: 0,
                c6Message: result.data.message || getString('ota.c6_updating'),
            })
            this.startC6Polling()
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    private handleC6PushStartButtonClick = async () => {
        try {
            const { c6SelectedId } = this.state
            if (c6SelectedId === undefined) throw new Error(getString('ota.select_software'))
            const full = (await getSoftwareVersion(c6SelectedId)).data
            const base64Bin = full.base64Bin
            if (!base64Bin) throw new Error(getString('ota.select_software'))
            const result = await startClientC6FirmwarePatch(this.props.clientId, base64Bin)
            console.log('startClientC6FirmwarePatch', result)
            if (result.data === StartErrorCode.AlreadyPatching) {
                throw new Error(getString('ota.start_err_already_patching'))
            }
            if (result.data === StartErrorCode.NotInSync) {
                throw new Error(getString('ota.start_err_not_in_sync'))
            }
            this.setState({
                c6Updating: true,
                c6Transport: 'push',
                c6Progress: 0,
                c6Message: getString('ota.c6_updating'),
            })
            this.startC6RawPolling()
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    private handleC6StopButtonClick = async () => {
        try {
            const { c6Transport } = this.state
            if (c6Transport === 'http') {
                const result = await cancelClientC6HttpOta(this.props.clientId)
                console.log('cancelClientC6HttpOta', result)
                this.stopC6Polling()
            } else {
                const result = await stopClientC6FirmwarePatch(this.props.clientId)
                console.log('stopClientC6FirmwarePatch', result)
                this.stopC6RawPolling()
            }
            this.setState({ c6Updating: false, c6Transport: undefined, c6Progress: undefined, c6Message: undefined })
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    private applyC6Status(status: HttpOtaStatus) {
        this.setState({
            c6Progress: status.progressPercent,
            c6Message: status.message || getString('ota.c6_updating'),
        })
        if (status.state === 3) {
            this.stopC6Polling()
            this.setState({ c6Updating: false, c6Progress: 100 })
            toast.success(getString('ota.c6_completed'))
            return
        }
        if (status.state === 4) {
            this.stopC6Polling()
            this.setState({ c6Updating: false, c6Message: undefined })
            throw new Error(status.message || 'C6 module update failed')
        }
        if (status.state === 5) {
            this.stopC6Polling()
            this.setState({ c6Updating: false, c6Message: undefined })
            toast.info(status.message || 'C6 OTA cancelled')
        }
    }

    private startC6Polling() {
        this.stopC6Polling()
        this.c6PollRef = setInterval(async () => {
            try {
                const status = (await getClientC6HttpOtaStatus(this.props.clientId)).data
                this.applyC6Status(status)
            } catch (err) {
                console.error(err)
                this.stopC6Polling()
                this.setState({ c6Updating: false, c6Message: undefined })
                toast.error((err as Error).message)
            }
        }, 1500)
    }

    private stopC6Polling() {
        if (this.c6PollRef !== undefined) {
            clearInterval(this.c6PollRef)
            this.c6PollRef = undefined
        }
    }

    /**
     * Raw push has no device-side status payload — progress streams over the
     * shared ws ota-progress events, and the server-side C6OtaManager keeps a
     * snapshot (progress / device rejections / timeouts / completion). This
     * poll drives progress + completion without ws and surfaces failures the
     * manager captured.
     */
    private startC6RawPolling() {
        this.stopC6RawPolling()
        this.c6RawPollRef = setInterval(async () => {
            try {
                const status = (await getClientC6RawOtaStatus(this.props.clientId)).data
                if (status.errorMessage) {
                    console.error(status.errorMessage)
                    this.stopC6RawPolling()
                    this.setState({ c6Updating: false, c6Transport: undefined, c6Progress: undefined, c6Message: undefined })
                    toast.error(status.errorMessage)
                    return
                }
                if (status.active) {
                    this.setState({ c6Progress: status.progress })
                    return
                }
                // Inactive + completed = the end response was received and the
                // machine is activating the C6 (it restarts ~1.5-3.5 s later).
                if (status.completed) {
                    this.stopC6RawPolling()
                    this.setState({ c6Updating: false, c6Transport: undefined, c6Progress: 100, c6Message: undefined })
                    toast.success(getString('ota.c6_completed'))
                }
            } catch (err) {
                console.error(err)
                this.stopC6RawPolling()
                this.setState({ c6Updating: false, c6Transport: undefined, c6Progress: undefined, c6Message: undefined })
                toast.error((err as Error).message)
            }
        }, 1500)
    }

    private stopC6RawPolling() {
        if (this.c6RawPollRef !== undefined) {
            clearInterval(this.c6RawPollRef)
            this.c6RawPollRef = undefined
        }
    }

    private renderC6Section() {
        const { c6Supported, c6Updating, c6Versions, c6SelectedId, c6Progress, c6Message } = this.state
        if (!c6Supported) return undefined
        const selectedRow = c6Versions.find(v => v.id === c6SelectedId)
        const busy = c6Updating === true || this.state.patching === true || this.state.restarting === true
        return (
            <div className="mb-6 rounded-xl border border-gray-300 dark:border-gray-600 p-4">
                <div className="flex items-center mb-1">
                    <span className="dark:text-white font-semibold">{getString('software_manager.component_c6')}</span>
                </div>
                <div className="mb-3 text-xs text-gray-500">{getString('ota.c6_subtitle')}</div>
                {c6Versions.length === 0 ? (
                    <div className="mb-3 text-sm text-gray-500 dark:text-gray-400">{getString('ota.c6_no_versions')}</div>
                ) : (
                    <>
                        <label className="dark:text-white text-sm">{getString('ota.c6_select_software')}</label>
                        <select
                            className="pretty-input"
                            value={c6SelectedId}
                            onChange={(e: any) => this.setState({ c6SelectedId: Number(e.target.value) })}
                            disabled={busy}
                        >
                            {c6Versions.map(v => (
                                <option key={v.id} value={v.id}>
                                    {v.version}
                                </option>
                            ))}
                        </select>
                        {selectedRow?.changeLog ? <div className="mt-2 mb-3 text-xs text-gray-500">{selectedRow.changeLog}</div> : null}
                        <div className="flex mt-2">
                            {this.renderButton(
                                getString('ota.c6_update_http'),
                                this.handleC6StartButtonClick,
                                busy || c6SelectedId === undefined,
                            )}
                            {this.renderButton(
                                getString('ota.c6_update_push'),
                                this.handleC6PushStartButtonClick,
                                busy || c6SelectedId === undefined,
                            )}
                            {this.renderButton(getString('ota.stop'), this.handleC6StopButtonClick, c6Updating !== true)}
                        </div>
                    </>
                )}
                {c6Updating === true && (
                    <>
                        <ProgressBar value={c6Progress ?? 0} className="mt-3 mb-3" />
                        {c6Message && <div className="mb-2 text-xs text-gray-400">{c6Message}</div>}
                    </>
                )}
            </div>
        )
    }

    private handleEspBinChange(binFile: MyFile, base64Bin: string, espApp: IEspApp) {
        this.setState({ base64Bin, binFile })
    }

    render() {
        const { patching } = this.state
        if (patching === undefined) return <Loader size={14} />
        return patching ? this.renderPatchActive() : this.renderPatchInactive()
    }

    private renderPatchInactive() {
        return (
            <>
                {this.renderC6Section()}
                {this.renderVersionCheck()}
                {this.renderMcuModel()}
                {this.renderEspBinInput()}
                {this.renderInactiveActions()}
            </>
        )
    }

    private renderVersionCheck() {
        const { outdated, latestSoftwareVersion } = this.state
        if (!outdated || latestSoftwareVersion === undefined) return undefined
        return <div className="mb-4 text-yellow-500">{getString('common.old_software_message', latestSoftwareVersion.version)}</div>
    }

    private renderMcuModel() {
        const { staticTraits } = this.state
        if (!staticTraits) return undefined
        return (
            <div className="mb-4 text-gray-500">
                {getString('static_traits.model')}: {ChipModel[staticTraits.model]}
            </div>
        )
    }

    private renderEspBinInput() {
        return (
            <EspBinInput
                ref={ref => (this.espBinRef = ref)}
                onChange={this.handleEspBinChange.bind(this)}
                binFile={this.state.binFile}
                disabled={this.state.patching}
            />
        )
    }

    private renderPatchActive() {
        return (
            <>
                {this.renderProgressBar()}
                {this.renderStopButton()}
            </>
        )
    }

    private renderProgressBar() {
        const { progress, httpOtaMessage } = this.state
        if (progress === undefined) return undefined
        return (
            <>
                <ProgressBar value={progress} className="mb-3" />
                {httpOtaMessage && <div className="mb-5 text-xs text-gray-400">{httpOtaMessage}</div>}
            </>
        )
    }

    private renderInactiveActions() {
        return (
            <div className="flex">
                {this.renderButton(getString('ota.select_software'), this.handleSoftwareSelectButton, this.state.restarting)}
                {this.renderButton(getString('ota.start_legacy'), this.handleStartLegacyButtonClick, this.state.restarting)}
                {this.renderButton(getString('ota.start_http'), this.handleStartHttpButtonClick, this.state.restarting)}
            </div>
        )
    }

    private renderStopButton() {
        return this.renderButton(getString('ota.stop'), this.handleStopButtonClick, this.state.restarting)
    }

    private renderButton(label: string, onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void, disabled = false) {
        return (
            <button
                onClick={onClick}
                className={classNames(
                    `me-4 group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-501`,
                    disabled ? 'cursor-not-allowed opacity-70' : `duration-200 hover:bg-indigo-700`,
                )}
                disabled={disabled}
            >
                {label}
            </button>
        )
    }
}

export default OtaTab
